import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  ClipboardList, Car, Timer, Eye, Heart, UserCheck,
  AlertTriangle, AlertCircle, Info, ChevronDown, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { ComprehensiveUserProfile, Issue, RoomAssessment } from './types';

interface AssessmentsTabProps {
  profile: ComprehensiveUserProfile;
}

const ImpactBadge: React.FC<{ impact: 'High' | 'Medium' | 'Low' }> = ({ impact }) => {
  const variants = {
    High: { icon: AlertTriangle, className: 'bg-destructive/10 text-destructive border-destructive/20' },
    Medium: { icon: AlertCircle, className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
    Low: { icon: Info, className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  };
  
  const { icon: Icon, className } = variants[impact];
  
  return (
    <Badge variant="outline" className={`${className} gap-1`}>
      <Icon className="h-3 w-3" />
      {impact}
    </Badge>
  );
};

const RoomCard: React.FC<{ room: RoomAssessment }> = ({ room }) => {
  const [isOpen, setIsOpen] = useState(false);
  const issues = room.analysisResults?.issues || [];
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border border-border/50">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <div>
                  <CardTitle className="text-sm font-medium">
                    {room.roomType?.replace(/_/g, ' ')}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {room.assessedAt ? format(new Date(room.assessedAt), 'MMM d, yyyy') : 'Unknown date'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {room.issuesSummary?.high > 0 && (
                  <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                    {room.issuesSummary.high} High
                  </Badge>
                )}
                {room.issuesSummary?.medium > 0 && (
                  <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs">
                    {room.issuesSummary.medium} Med
                  </Badge>
                )}
                <Badge variant="secondary" className="text-xs">
                  {room.issuesSummary?.total || 0} issues
                </Badge>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            {issues.length === 0 ? (
              <p className="text-sm text-muted-foreground">No issues found</p>
            ) : (
              <div className="space-y-3">
                {issues.map((issue: Issue, idx: number) => (
                  <div key={issue.id || idx} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium">{issue.title}</h4>
                      <ImpactBadge impact={issue.impact} />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {issue.primaryRecommendation}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export const AssessmentsTab: React.FC<AssessmentsTabProps> = ({ profile }) => {
  const { roomAssessments, drivingAssessments, tugTests, visionAssessments, adlAssessments, iadlAssessments } = profile;

  return (
    <div className="space-y-6">
      {/* Room Assessments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            Room Scans ({roomAssessments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {roomAssessments?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roomAssessments.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No room checks completed</p>
          )}
        </CardContent>
      </Card>

      {/* Other Assessments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Driving Assessments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Car className="h-4 w-4 text-purple-500" />
              Driving Assessments ({drivingAssessments?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {drivingAssessments?.length > 0 ? (
              drivingAssessments.map((da) => (
                <div key={da.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant={
                      da.resultCategory === 'SAFE_DRIVER' ? 'default' :
                      da.resultCategory === 'CAUTION_ADVISED' ? 'secondary' : 'destructive'
                    }>
                      {da.resultCategory?.replace(/_/g, ' ')}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {da.completedAt ? format(new Date(da.completedAt), 'MMM d, yyyy') : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold">{da.totalScore}</span>
                    <span className="text-muted-foreground text-sm">/ {da.maxPossibleScore}</span>
                    <span className="text-muted-foreground text-sm ml-2">({da.percentage}%)</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No driving assessments</p>
            )}
          </CardContent>
        </Card>

        {/* TUG Tests */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Timer className="h-4 w-4 text-cyan-500" />
              TUG Tests ({tugTests?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {tugTests?.length > 0 ? (
              tugTests.map((tt) => (
                <div key={tt.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant={
                      tt.riskLevel === 'LOW' ? 'default' :
                      tt.riskLevel === 'MODERATE' ? 'secondary' : 'destructive'
                    }>
                      {tt.riskLevel} Risk
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {tt.performedAt ? format(new Date(tt.performedAt), 'MMM d, yyyy') : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold">{tt.timeSeconds}</span>
                    <span className="text-muted-foreground text-sm">seconds</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No TUG tests</p>
            )}
          </CardContent>
        </Card>

        {/* Vision Assessments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-500" />
              Vision Assessments ({visionAssessments?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {visionAssessments?.length > 0 ? (
              visionAssessments.map((va) => (
                <div key={va.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-muted-foreground">
                      {va.assessedAt ? format(new Date(va.assessedAt), 'MMM d, yyyy') : 'Unknown'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">Left Eye</p>
                      <p className="font-bold">{va.leftEyeScore}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Right Eye</p>
                      <p className="font-bold">{va.rightEyeScore}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Overall</p>
                      <p className="font-bold">{va.overallScore}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No vision assessments</p>
            )}
          </CardContent>
        </Card>

        {/* ADL/IADL Assessments */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-orange-500" />
              ADL/IADL Assessments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* ADL */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">ADL ({adlAssessments?.length || 0})</h4>
              {adlAssessments?.length > 0 ? (
                <div className="space-y-2">
                  {adlAssessments.slice(0, 3).map((adl) => (
                    <div key={adl.id} className="p-2 rounded bg-muted/30 border border-border/50 flex justify-between items-center">
                      <div>
                        <Badge variant="outline" className="text-xs">{adl.dependencyLevel}</Badge>
                        <span className="text-xs text-muted-foreground ml-2">
                          {adl.completedAt ? format(new Date(adl.completedAt), 'MMM d') : ''}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{adl.totalScore}/{adl.maxScore}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No ADL assessments</p>
              )}
            </div>
            
            {/* IADL */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">IADL ({iadlAssessments?.length || 0})</h4>
              {iadlAssessments?.length > 0 ? (
                <div className="space-y-2">
                  {iadlAssessments.slice(0, 3).map((iadl) => (
                    <div key={iadl.id} className="p-2 rounded bg-muted/30 border border-border/50 flex justify-between items-center">
                      <div>
                        <Badge variant="outline" className="text-xs">{iadl.independenceLevel}</Badge>
                        <span className="text-xs text-muted-foreground ml-2">
                          {iadl.completedAt ? format(new Date(iadl.completedAt), 'MMM d') : ''}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{iadl.totalScore}/{iadl.maxScore}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No IADL assessments</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
